// baseQueryWithReauth.ts
import { fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { authApi } from './auth';

// Define types for subscribers
type SubscriberCallback = () => void;

let isRefreshing = false; // Track refresh token status
let subscribers: SubscriberCallback[] = [];

const onRefreshed = (callback: SubscriberCallback) => {
  subscribers.push(callback);
};

// Notify all subscribers
const notifySubscribers = () => {
  subscribers.forEach((callback) => callback());
  subscribers = []; // Reset subscriber list
};

// Define the base query with proper typing
const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL,
  prepareHeaders: (headers: Headers) => {
    // Get token from localStorage and add to Authorization header
    const token = localStorage.getItem('accessToken');
    if (token) {
      headers.set('Authorization', `Bearer ${token}`); // Changed from 'token' to 'Authorization'
    }
    return headers;
  },
  // credentials: 'include', // Include cookies with requests
});

// Define the return type for refresh token result - API chỉ trả về accessToken
interface RefreshTokenResponse {
  accessToken: string;
}

// Extend the base query with re-authentication logic
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  
  if (result.error && (result.error.status === 401 || result.error.status === 403)) {
    if (!isRefreshing) {
      isRefreshing = true;
        const refreshToken = localStorage.getItem('refreshToken');
      
      try {
        // Lấy refresh token từ localStorage
        
        if (!refreshToken) {
          // Không có refresh token, logout user
          api.dispatch(authApi.endpoints.logout.initiate({token:refreshToken}));
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          api.dispatch({ type: 'auth/logout' });
          isRefreshing = false;
          return result;
        }

        // Gọi API refresh token với refresh token trong body
        const refreshResult = await api.dispatch(
          authApi.endpoints.refreshToken.initiate({ token: refreshToken })
        );

        if (refreshResult.data) {
          // API chỉ trả về accessToken (string), không phải object
          const newAccessToken = refreshResult.data.accessToken as string;
          localStorage.setItem('accessToken', newAccessToken);

          // Retry original request với access token mới
          result = await baseQuery(args, api, extraOptions);
          notifySubscribers();
        } else {
          // Refresh token không hợp lệ, logout user
          api.dispatch(authApi.endpoints.logout.initiate({token:refreshToken}));
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          api.dispatch({ type: 'auth/logout' });
        }
      } catch (error) {
        // Lỗi khi refresh token, logout user
        api.dispatch(authApi.endpoints.logout.initiate({token: refreshToken}));
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        api.dispatch({ type: 'auth/logout' });
      }
      
      isRefreshing = false;
    } else {
      // Đang refresh token, đợi kết quả
      return new Promise((resolve) => {
        onRefreshed(async () => {
          const retryResult = await baseQuery(args, api, extraOptions);
          resolve(retryResult);
        });
      });
    }
  }

  return result;
};